import {
  getSPBlobWithParams,
  getSPDataWithParams,
  postSPDataWithParams,
  postSPFormDataWithoutParams,
} from './services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSupportRole } from 'src/shared/config/calendarReportApiConfig'

// Unified support report-PDF download endpoint (therapy/screening/assessment/prescription).
const REPORT_PDF_DOWNLOAD_PATH = '/api/secure/cc/support/practitioner/emr-reports/download'

// Base API paths for support portal
// Default support roles use: /api/secure/cc/support/emr-reports
// CSA/CSM prescription APIs use: /api/secure/cc/support/csm/emr-reports
const API_BASE = '/api/secure/cc/support/emr-reports'
const CLINIC_SUCCESS_PRESCRIPTION_API_BASE = '/api/secure/cc/support/csm/emr-reports'

/**
 * Get the appropriate API base path based on support role
 * Clinic Success Manager/Admin uses different base path
 * @returns {string} API base path
 */
function getApiBasePath(reportType) {
  const supportRole = getSupportRole()
  const isClinicSuccess = supportRole === 'ROLE_CLINIC_SUCCESS_MANAGER' || supportRole === 'ROLE_CLINIC_SUCCESS_ADMIN'
  if (reportType === 'prescription') {
    return isClinicSuccess ? CLINIC_SUCCESS_PRESCRIPTION_API_BASE : null
  }
  return API_BASE
}

/**
 * Fetch therapy report images for a given event
 * @param {string} eventId - The booked event ID
 * @returns {Promise<{fileIds: string[], signedUrls: string[]}>} Object containing file IDs and signed URLs
 */
export async function fetchTherapyReportImages(eventId) {
  try {
    const apiBase = getApiBasePath('therapy-report')
    const resp = await getSPDataWithParams(
      `${apiBase}/therapy-report/images/get-by-eventId`,
      { eventId }
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const data = getDataMapFromCommonResponse(resp)
      return {
        success: true,
        fileIds: data?.reportImages ?? [],
        signedUrls: data?.signedURLs ?? [],
        code: resp?.data?.code,
        message: resp?.data?.message,
      }
    }

    return {
      success: false,
      fileIds: [],
      signedUrls: [],
      error: resp?.data?.message || 'Failed to fetch therapy report images',
    }
  } catch (error) {
    console.error('Error fetching therapy report images:', error)
    return {
      success: false,
      fileIds: [],
      signedUrls: [],
      error: error?.message || 'Network error while fetching images',
    }
  }
}

/**
 * Fetch screening report images for a given event
 * @param {string} eventId - The booked event ID
 * @returns {Promise<{fileIds: string[], signedUrls: string[]}>} Object containing file IDs and signed URLs
 */
export async function fetchScreeningReportImages(eventId) {
  try {
    const apiBase = getApiBasePath('screening-report')
    const resp = await getSPDataWithParams(
      `${apiBase}/screening-report/images/get-by-eventId`,
      { eventId }
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const data = getDataMapFromCommonResponse(resp)
      return {
        success: true,
        fileIds: data?.reportImages ?? [],
        signedUrls: data?.signedURLs ?? [],
        code: resp?.data?.code,
        message: resp?.data?.message,
      }
    }

    return {
      success: false,
      fileIds: [],
      signedUrls: [],
      error: resp?.data?.message || 'Failed to fetch screening report images',
    }
  } catch (error) {
    console.error('Error fetching screening report images:', error)
    return {
      success: false,
      fileIds: [],
      signedUrls: [],
      error: error?.message || 'Network error while fetching images',
    }
  }
}

/**
 * Save therapy report images for a given event
 * @param {string} eventId - The booked event ID
 * @param {string[]} fileIds - Array of AppFile IDs to save (ordered list)
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export async function saveTherapyReportImages(eventId, fileIds) {
  try {
    const apiBase = getApiBasePath('therapy-report')
    const resp = await postSPDataWithParams(
      `${apiBase}/therapy-report/images/save`,
      fileIds, // Body is the array of file IDs
      { eventId } // Query params
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      return {
        success: true,
        message: resp?.data?.message || 'Therapy report images saved successfully',
        code: resp?.data?.code,
      }
    }

    return {
      success: false,
      error: resp?.data?.message || 'Failed to save therapy report images',
    }
  } catch (error) {
    console.error('Error saving therapy report images:', error)
    return {
      success: false,
      error: error?.message || 'Network error while saving images',
    }
  }
}

/**
 * Save screening report images for a given event
 * @param {string} eventId - The booked event ID
 * @param {string[]} fileIds - Array of AppFile IDs to save (ordered list)
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export async function saveScreeningReportImages(eventId, fileIds) {
  try {
    const apiBase = getApiBasePath('screening-report')
    const resp = await postSPDataWithParams(
      `${apiBase}/screening-report/images/save`,
      fileIds, // Body is the array of file IDs
      { eventId } // Query params
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      return {
        success: true,
        message: resp?.data?.message || 'Screening report images saved successfully',
        code: resp?.data?.code,
      }
    }

    return {
      success: false,
      error: resp?.data?.message || 'Failed to save screening report images',
    }
  } catch (error) {
    console.error('Error saving screening report images:', error)
    return {
      success: false,
      error: error?.message || 'Network error while saving images',
    }
  }
}

/**
 * Fetch assessment report images for a given event
 * @param {string} eventId - The booked event ID
 * @returns {Promise<{fileIds: string[], signedUrls: string[]}>} Object containing file IDs and signed URLs
 */
export async function fetchAssessmentReportImages(eventId) {
  try {
    const apiBase = getApiBasePath('assessment-report')
    const resp = await getSPDataWithParams(
      `${apiBase}/assessment-report/images/get-by-eventId`,
      { eventId }
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const data = getDataMapFromCommonResponse(resp)
      return {
        success: true,
        fileIds: data?.reportImages ?? [],
        signedUrls: data?.signedURLs ?? [],
        code: resp?.data?.code,
        message: resp?.data?.message,
      }
    }

    return {
      success: false,
      fileIds: [],
      signedUrls: [],
      error: resp?.data?.message || 'Failed to fetch assessment report images',
    }
  } catch (error) {
    console.error('Error fetching assessment report images:', error)
    return {
      success: false,
      fileIds: [],
      signedUrls: [],
      error: error?.message || 'Network error while fetching images',
    }
  }
}

/**
 * Fetch investigation report images for a given event
 * @param {string} eventId - The booked event ID
 * @returns {Promise<{fileIds: string[], signedUrls: string[]}>} Object containing file IDs and signed URLs
 */
export async function fetchInvestigationReportImages(eventId) {
  try {
    const apiBase = getApiBasePath('investigation-report')
    const resp = await getSPDataWithParams(
      `${apiBase}/investigation-report/images/get-by-eventId`,
      { eventId }
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const data = getDataMapFromCommonResponse(resp)
      return {
        success: true,
        fileIds: data?.reportImages ?? [],
        signedUrls: data?.signedURLs ?? [],
        code: resp?.data?.code,
        message: resp?.data?.message,
      }
    }

    return {
      success: false,
      fileIds: [],
      signedUrls: [],
      error: resp?.data?.message || 'Failed to fetch investigation report images',
    }
  } catch (error) {
    console.error('Error fetching investigation report images:', error)
    return {
      success: false,
      fileIds: [],
      signedUrls: [],
      error: error?.message || 'Network error while fetching images',
    }
  }
}

/**
 * Save assessment report images for a given event
 * @param {string} eventId - The booked event ID
 * @param {string[]} fileIds - Array of AppFile IDs to save (ordered list)
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export async function saveAssessmentReportImages(eventId, fileIds) {
  try {
    const apiBase = getApiBasePath('assessment-report')
    const resp = await postSPDataWithParams(
      `${apiBase}/assessment-report/images/save`,
      fileIds, // Body is the array of file IDs
      { eventId } // Query params
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      return {
        success: true,
        message: resp?.data?.message || 'Assessment report images saved successfully',
        code: resp?.data?.code,
      }
    }

    return {
      success: false,
      error: resp?.data?.message || 'Failed to save assessment report images',
    }
  } catch (error) {
    console.error('Error saving assessment report images:', error)
    return {
      success: false,
      error: error?.message || 'Network error while saving images',
    }
  }
}

/**
 * Save investigation report images for a given event
 * @param {string} eventId - The booked event ID
 * @param {string[]} fileIds - Array of AppFile IDs to save (ordered list)
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export async function saveInvestigationReportImages(eventId, fileIds) {
  try {
    const apiBase = getApiBasePath('investigation-report')
    const resp = await postSPDataWithParams(
      `${apiBase}/investigation-report/images/save`,
      fileIds, // Body is the array of file IDs
      { eventId } // Query params
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      return {
        success: true,
        message: resp?.data?.message || 'Investigation report images saved successfully',
        code: resp?.data?.code,
      }
    }

    return {
      success: false,
      error: resp?.data?.message || 'Failed to save investigation report images',
    }
  } catch (error) {
    console.error('Error saving investigation report images:', error)
    return {
      success: false,
      error: error?.message || 'Network error while saving images',
    }
  }
}

/**
 * Fetch prescription report images for a given event
 * @param {string} eventId - The booked event ID
 * @returns {Promise<{fileIds: string[], signedUrls: string[]}>} Object containing file IDs and signed URLs
 */
export async function fetchPrescriptionReportImages(eventId) {
  try {
    const apiBase = getApiBasePath('prescription')
    if (!apiBase) {
      return {
        success: false,
        fileIds: [],
        signedUrls: [],
        error: 'Prescription report images are only available for clinic success roles',
      }
    }
    const resp = await getSPDataWithParams(
      `${apiBase}/prescription/images/get-by-eventId`,
      { eventId }
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const data = getDataMapFromCommonResponse(resp)
      return {
        success: true,
        fileIds: data?.reportImages ?? [],
        signedUrls: data?.signedURLs ?? [],
        code: resp?.data?.code,
        message: resp?.data?.message,
      }
    }

    return {
      success: false,
      fileIds: [],
      signedUrls: [],
      error: resp?.data?.message || 'Failed to fetch prescription report images',
    }
  } catch (error) {
    console.error('Error fetching prescription report images:', error)
    return {
      success: false,
      fileIds: [],
      signedUrls: [],
      error: error?.message || 'Network error while fetching images',
    }
  }
}

/**
 * Save prescription report images for a given event
 * @param {string} eventId - The booked event ID
 * @param {string[]} fileIds - Array of AppFile IDs to save (ordered list)
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export async function savePrescriptionReportImages(eventId, fileIds) {
  try {
    const apiBase = getApiBasePath('prescription')
    if (!apiBase) {
      return {
        success: false,
        error: 'Prescription report images are only available for clinic success roles',
      }
    }
    const resp = await postSPDataWithParams(
      `${apiBase}/prescription/images/save`,
      fileIds, // Body is the array of file IDs
      { eventId } // Query params
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      return {
        success: true,
        message: resp?.data?.message || 'Prescription report images saved successfully',
        code: resp?.data?.code,
      }
    }

    return {
      success: false,
      error: resp?.data?.message || 'Failed to save prescription report images',
    }
  } catch (error) {
    console.error('Error saving prescription report images:', error)
    return {
      success: false,
      error: error?.message || 'Network error while saving images',
    }
  }
}

/**
 * Fetch images based on event type
 * @param {string} eventType - Event type ('therapy-session', 'screening', 'assessment', 'investigation', 'consultation', 'prescription')
 * @param {string} eventId - The booked event ID
 * @returns {Promise<{fileIds: string[], signedUrls: string[]}>}
 */
export async function fetchReportImagesByEventType(eventType, eventId) {
  if (eventType === 'therapy-session' || eventType === 'therapy-report') {
    return await fetchTherapyReportImages(eventId)
  } else if (eventType === 'screening' || eventType === 'screening-report') {
    return await fetchScreeningReportImages(eventId)
  } else if (eventType === 'assessment' || eventType === 'assessment-report') {
    return await fetchAssessmentReportImages(eventId)
  } else if (eventType === 'investigation' || eventType === 'investigation-report') {
    return await fetchInvestigationReportImages(eventId)
  } else if (eventType === 'consultation' || eventType === 'prescription') {
    return await fetchPrescriptionReportImages(eventId)
  }
  
  return {
    success: false,
    fileIds: [],
    signedUrls: [],
    error: `Unsupported event type: ${eventType}`,
  }
}

/**
 * Save images based on event type
 * @param {string} eventType - Event type ('therapy-session', 'screening', 'assessment', 'investigation', 'consultation', 'prescription')
 * @param {string} eventId - The booked event ID
 * @param {string[]} fileIds - Array of AppFile IDs to save
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export async function saveReportImagesByEventType(eventType, eventId, fileIds) {
  if (eventType === 'therapy-session' || eventType === 'therapy-report') {
    return await saveTherapyReportImages(eventId, fileIds)
  } else if (eventType === 'screening' || eventType === 'screening-report') {
    return await saveScreeningReportImages(eventId, fileIds)
  } else if (eventType === 'assessment' || eventType === 'assessment-report') {
    return await saveAssessmentReportImages(eventId, fileIds)
  } else if (eventType === 'investigation' || eventType === 'investigation-report') {
    return await saveInvestigationReportImages(eventId, fileIds)
  } else if (eventType === 'consultation' || eventType === 'prescription') {
    return await savePrescriptionReportImages(eventId, fileIds)
  }
  
  return {
    success: false,
    error: `Unsupported event type: ${eventType}`,
  }
}

/**
 * Upload a scanned report document (PDF or image) against a booking. The backend derives the
 * patient/practitioner from the booking; prescription uploads route to the clinic-success base,
 * everything else to the default support base. Returns { success, fileId, appFile }.
 * @param {string} reportType - EMR report type ('prescription' | 'therapy-report' | 'screening-report' | 'assessment-report')
 * @param {string} bookedEventId - The booked event ID
 * @param {File} file - The PDF/image to upload
 * @param {number} [sequenceNumber=1] - Ordering hint
 */
export async function uploadSupportReportFile(reportType, bookedEventId, file, sequenceNumber = 1) {
  try {
    const apiBase = getApiBasePath(reportType)
    if (!apiBase) {
      return {
        success: false,
        error: 'Uploads for this report type are only available for clinic success roles',
      }
    }
    const formData = new FormData()
    formData.append('file', file)
    const query = new URLSearchParams({
      bookedEventId,
      emrReportType: reportType,
      'sequence-number': String(sequenceNumber),
    })
    // The upload endpoint returns a raw AppFile (not a CommonResponse envelope).
    const resp = await postSPFormDataWithoutParams(`${apiBase}/file/upload?${query.toString()}`, formData)
    if (resp?.status === 200 && resp?.data?.id) {
      return { success: true, fileId: resp.data.id, appFile: resp.data }
    }
    return {
      success: false,
      error: resp?.data?.message || resp?.response?.data?.message || 'Failed to upload the document',
    }
  } catch (error) {
    console.error('Error uploading support report file:', error)
    return { success: false, error: error?.message || 'Network error while uploading' }
  }
}

function filenameFromContentDisposition(headerValue, fallback) {
  const raw = String(headerValue || '')
  const match = raw.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
  return match ? decodeURIComponent(match[1]) : fallback
}

async function readBlobErrorMessage(blob, fallback) {
  try {
    const text = await blob.text()
    const parsed = JSON.parse(text)
    return parsed?.message || fallback
  } catch {
    return fallback
  }
}

/**
 * Download the server-generated report PDF for a booking. Returns { success, blob, filename }.
 * @param {string} reportType - EMR report type ('prescription' | 'therapy-report' | 'screening-report' | 'assessment-report')
 * @param {string} eventId - The booked event ID
 */
export async function downloadSupportReportPdf(reportType, eventId) {
  try {
    const resp = await getSPBlobWithParams(REPORT_PDF_DOWNLOAD_PATH, { reportType, eventId })
    if (resp?.status === 200 && resp?.data) {
      return {
        success: true,
        blob: resp.data,
        filename: filenameFromContentDisposition(
          resp.headers?.['content-disposition'],
          `${reportType}.pdf`,
        ),
      }
    }
    return { success: false, error: 'Failed to download the report PDF' }
  } catch (error) {
    const errorBlob = error?.response?.data
    const fallback = 'Could not download the report PDF right now.'
    if (errorBlob instanceof Blob) {
      return { success: false, error: await readBlobErrorMessage(errorBlob, fallback) }
    }
    return { success: false, error: error?.message || fallback }
  }
}


